import { EBlockType } from 'common/TemplateStudio.types';
import { BodyBlockId } from 'constants/bodyBlocks';
import { EPosition } from 'constants/enums';
import { z } from 'zod';

import { GradientDirection } from 'components/AcGradientInput/AcGraidentInput.types';
import {
  ETemplateStudioBundlesViewModel,
  ETemplateStudioLayoutOptions,
  EThemes
} from 'components/TemplatesStudio/enums';
import type { IFormStoreBlock } from 'components/TemplatesStudio/multiInstanceBlocks/multiInstanceBlocks.types';
const gradientSchema = z.object({
  colorOne: z.string().min(1, { message: 'Color is required.' }),
  colorTwo: z.string().optional(),
  gradientDirection: z.nativeEnum(GradientDirection).optional()
});

export const onePlusManyInstanceSchema = z.object({
  desktopLayout: z.nativeEnum(ETemplateStudioLayoutOptions),
  themes: z.array(z.nativeEnum(EThemes)),
  selectedDesignId: z.string().nonempty('This field is required.'),
  maxOffers: z.object({
    unlimited: z.boolean(),
    value: z
      .number()
      .int({ message: 'Max offers must be a whole number.' })
      .positive({ message: 'Max offers must be at least 1.' })
  }),
  rewardsLayout: z.union([z.literal(1), z.literal(2), z.literal(3)])
});

export const onePlusManyFormStoreBlockSchema = onePlusManyInstanceSchema.extend(
  {
    id: z.string(),
    instanceKey: z.string(),
    type: z.literal(EBlockType.ONE_PLUS_MANY),
    isVisible: z.boolean(),
    mergedTo: z.array(z.string()).optional()
  }
);

const formStoreBlockSchema = z.custom<IFormStoreBlock>(
  (value) => typeof value === 'object' && value !== null
);

const storeBlocksSchema = z
  .array(formStoreBlockSchema)
  .superRefine((blocks, ctx) => {
    blocks.forEach((block, index) => {
      if (block.type !== EBlockType.ONE_PLUS_MANY) return;
      const result = onePlusManyInstanceSchema.safeParse(block);
      if (result.success) return;
      result.error.issues.forEach((issue) => {
        ctx.addIssue({ ...issue, path: [index, ...issue.path] });
      });
    });
  });

const bodyBlockIdSchema = z.custom<BodyBlockId>(
  (value) => typeof value === 'string'
);

export const templateStudioSchema = z
  .object({
    templateName: z.string().min(1, { message: 'This field is required.' }),
    description: z.string(),
    tags: z.array(z.string()),
    bodyBlocksOrder: z.array(bodyBlockIdSchema),
    bodyBlocksOrderV2: z.array(
      z.object({
        id: z.string().optional(),
        blocks: z.array(bodyBlockIdSchema),
        isVisible: z.boolean(),
        isMergeable: z.boolean()
      })
    ),
    storeBlocks: storeBlocksSchema.optional(),
    header: z.object({
      showLogo: z.boolean(),
      showPlayerName: z.boolean(),
      showPlayerId: z.boolean(),
      isSticky: z.boolean(),
      backgroundOpacity: z
        .number()
        .min(0, { message: 'Opacity must be at least 0%' })
        .max(100, { message: 'Opacity must be at most 100%' }),
      textColor: gradientSchema
    }),
    promotion: z.object({
      themes: z.array(z.nativeEnum(EThemes)),
      selectedDesignId: z.string().nonempty('This field is required.'),
      isCarouselView: z.boolean(),
      slidesPerViewDesktop: z.union([z.literal(1), z.literal(2), z.literal(3)])
    }),
    bundle: z.object({
      offersPerRow: z.nativeEnum(ETemplateStudioBundlesViewModel).nullable(),
      bundleViewModel: z.nativeEnum(ETemplateStudioBundlesViewModel),
      themes: z.array(z.nativeEnum(EThemes)),
      selectedDesignId: z.string().nonempty('This field is required.')
    }),
    popup: z.object({
      selectedDesignId: z.string().nonempty('This field is required.')
    }),
    rollingOffers: z.object({
      desktopLayout: z.nativeEnum(ETemplateStudioLayoutOptions),
      themes: z.array(z.nativeEnum(EThemes)),
      selectedDesignId: z.string().nonempty('This field is required.')
    }),
    progressBar: z.object({
      selectedDesignId: z.string().nonempty('This field is required.')
    }),
    richText: z
      .object({
        text: z.string(),
        fontColor: z.string()
      })
      .optional(),
    dailyBonusBadge: z.object({
      themes: z.array(z.nativeEnum(EThemes)),
      selectedDesignId: z.string().nonempty('This field is required.')
    }),
    triggeredPopups: z.object({
      selectedDesignId: z.string().nonempty('This field is required.'),
      selectedBadgeDesignId: z.string().nonempty('This field is required.'),
      themes: z.array(z.nativeEnum(EThemes))
    }),
    rewardCalendar: z.object({
      selectedDesignId: z.string().nonempty('This field is required.'),
      selectedBadgeDesignId: z.string().nonempty('This field is required.'),
      themes: z.array(z.nativeEnum(EThemes))
    }),
    footer: z.object({
      showAppchargeLogo: z.boolean(),
      backgroundOpacity: z.number().min(0).max(100),
      textColor: gradientSchema
    }),
    brandKit: z.object({
      fontFamily: z.string(),
      buttonColor: gradientSchema,
      buttonTextColor: z.string(),
      logo: z.string().min(1, { message: 'This field is required.' }),
      backgroundImageMobile: z
        .string()
        .min(1, { message: 'This field is required.' }),
      backgroundImageDesktop: z
        .string()
        .min(1, { message: 'This field is required.' }),
      defaultBanner: z.string(),
      footerDesktopImage: z.string(),
      footerMobileImage: z.string(),
      customArrowImage: z.string().optional()
    }),
    externalButtons: z.object({
      verticalOffset: z
        .number()
        .min(0, { message: 'Vertical Offset must be at least 0%' })
        .max(100, { message: 'Vertical Offset must be at most 100%' }),
      addToHomeScreenButton: z
        .object({
          active: z.boolean(),
          borderColor: gradientSchema,
          backgroundColor: gradientSchema,
          buttonImage: z.string().optional(),
          iconImage: z.string().optional(),
          shortcutName: z.string().optional()
        })
        .refine(
          (data) => !data.active || (data.iconImage?.trim().length ?? 0) > 0,
          {
            message: 'Icon Image is required when the feature is activated.',
            path: ['iconImage']
          }
        )
        .refine(
          (data) => !data.active || (data.shortcutName?.trim().length ?? 0) > 0,
          {
            message: 'Store Title is required when the feature is activated.',
            path: ['shortcutName']
          }
        ),
      backToGameButton: z
        .object({
          active: z.boolean(),
          buttonImage: z.string().optional(),
          position: z.nativeEnum(EPosition)
        })
        .refine(
          (data) => !data.active || (data.buttonImage?.trim().length ?? 0) > 0,
          {
            message: 'Button Image is required when the feature is activated.',
            path: ['buttonImage']
          }
        )
        .refine(
          (data) => !data.active || (data.position?.trim().length ?? 0) > 0,
          {
            message: 'Position is required when the feature is activated.',
            path: ['position']
          }
        )
    })
  })
  .passthrough();
